import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const homeSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      name: 'PowerLottery.co',
      url: siteUrl,
      description: 'Smarter lottery analytics for POWERBALL and MegaMillions',
    },
    {
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'PowerLottery.co',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://public.readdy.ai/ai/img_res/a5d5b9d0-9163-482d-be03-b47dc644bd65.png',
      },
    },
    {
      '@type': 'Product',
      name: 'PowerLottery.co Weekly Analytics License',
      description:
        '52 on-demand weekly reports with predictive analysis, hot numbers, cold numbers, and trend shifts for POWERBALL and MegaMillions lottery draws.',
      brand: {
        '@type': 'Brand',
        name: 'PowerLottery.co',
      },
      offers: {
        '@type': 'Offer',
        price: '79.00',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: siteUrl,
      },
    },
  ],
};

export default function HomePage() {
  const [paypalStatus, setPaypalStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const paypalRendered = useRef(false);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 20;

    const tryRender = () => {
      const win = window as unknown as Record<string, unknown>;
      const container = document.getElementById('paypal-container-8M77GKTPHMP4A');
      const paypal = win['paypal'] as { HostedButtons?: (opts: { hostedButtonId: string }) => { render: (sel: string) => Promise<void> } } | undefined;

      if (paypalRendered.current) return;

      if (paypal?.HostedButtons && container) {
        paypalRendered.current = true;
        paypal
          .HostedButtons({ hostedButtonId: '8M77GKTPHMP4A' })
          .render('#paypal-container-8M77GKTPHMP4A')
          .then(() => setPaypalStatus('ready'))
          .catch(() => {
            paypalRendered.current = false;
            setPaypalStatus('error');
          });
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        setTimeout(tryRender, 500);
      } else {
        setPaypalStatus('error');
      }
    };

    tryRender();
  }, []);

  const retryPaypal = () => {
    paypalRendered.current = false;
    setPaypalStatus('loading');
    const container = document.getElementById('paypal-container-8M77GKTPHMP4A');
    if (container) container.innerHTML = '';

    let attempts = 0;
    const maxAttempts = 20;
    const win = window as unknown as Record<string, unknown>;

    const tryRender = () => {
      const paypal = win['paypal'] as { HostedButtons?: (opts: { hostedButtonId: string }) => { render: (sel: string) => Promise<void> } } | undefined;
      const el = document.getElementById('paypal-container-8M77GKTPHMP4A');

      if (paypalRendered.current) return;

      if (paypal?.HostedButtons && el) {
        paypalRendered.current = true;
        paypal
          .HostedButtons({ hostedButtonId: '8M77GKTPHMP4A' })
          .render('#paypal-container-8M77GKTPHMP4A')
          .then(() => setPaypalStatus('ready'))
          .catch(() => {
            paypalRendered.current = false;
            setPaypalStatus('error');
          });
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        setTimeout(tryRender, 500);
      } else {
        setPaypalStatus('error');
      }
    };

    tryRender();
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    isSubscriber: false
  });
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : false;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === 'email') {
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('Please fill in all required fields');
      return;
    }

    if (!validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const submitData = new URLSearchParams();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('message', formData.message);
      submitData.append('isSubscriber', formData.isSubscriber ? 'Yes' : 'No');
      submitData.append('mailto', 'land30163@outlook.com');
      submitData.append('subject', 'PowerLottery.co Support');

      const response = await fetch('https://readdy.ai/api/form/d5qdvdb592clg457kce0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setSubmitStatus('Thank you! Your message has been sent successfully.');
        setFormData({ name: '', email: '', message: '', isSubscriber: false });
        setEmailError('');
      } else {
        setSubmitStatus('There was an error sending your message. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('There was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <>
      <title>PowerLottery.co – Smarter POWERBALL &amp; MegaMillions Lottery Analytics</title>
      <meta name="description" content="PowerLottery.co uses proprietary analytics to identify patterns in POWERBALL and MegaMillions lottery draws. Get hot numbers, cold numbers, trend analysis, and weekly predictive reports. $79 for 52 reports." />
      <meta name="keywords" content="lottery analytics, powerball predictions, megamillions analysis, lottery number trends" />
      <link rel="canonical" href={`${siteUrl}/`} />
      <meta property="og:url" content={`${siteUrl}/`} />
      <meta property="og:title" content="PowerLottery.co – Smarter POWERBALL &amp; MegaMillions Lottery Analytics" />
      <meta property="og:description" content="Proprietary analytics that identify patterns, hot numbers, cold numbers, and trend shifts in POWERBALL and MegaMillions. 52 on-demand weekly reports for $79." />
      <meta name="twitter:title" content="PowerLottery.co – Smarter POWERBALL &amp; MegaMillions Lottery Analytics" />
      <meta name="twitter:description" content="Proprietary analytics that identify patterns, hot numbers, cold numbers, and trend shifts in POWERBALL and MegaMillions. 52 on-demand weekly reports for $79." />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }}
      />
      <div className="min-h-screen bg-white font-['Inter',sans-serif]">
        {/* Header */}
        <header className="bg-black py-12 text-center border-b border-gray-100">
          <div className="flex justify-center items-center mb-6">
            <img 
              src="https://public.readdy.ai/ai/img_res/a5d5b9d0-9163-482d-be03-b47dc644bd65.png" 
              alt="PowerLottery.co Logo" 
              className="w-16 h-16 mr-4 object-contain"
            />
            <h1 className="text-5xl font-bold">
              <span className="text-white bg-black px-2 py-1 rounded-lg mr-1">Power</span>
              <span className="text-[#FF69B4]">Lottery.co</span>
            </h1>
          </div>
          <p className="text-2xl text-white font-medium">Smarter Lottery Players</p>
        </header>

        {/* Main Content - 3 Columns */}
        <main className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Column 1 */}
            <section className="text-center">
              <div className="bg-gray-50 rounded-xl p-8 h-full flex flex-col items-start justify-start">
                <p className="text-gray-700 text-lg leading-relaxed text-left mb-4">
                  PowerLottery.co uses proprietary analytics to identify patterns in POWERBALL and MEGAMILLIONS lottery numbers and makes predictive analysis of future drawings. Our goal is to make you a smarter lottery player. We identify changes, hot numbers, cold numbers, numbers trending up or down, significant anomalies and shifts in drawing patterns.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed text-left mb-4">
                  PowerLottery is easy to use. See our{' '}
                  <Link to="/user-guide" className="text-[#FF69B4] hover:underline cursor-pointer font-medium">User Guide</Link>
                  {' '}for step-by-step instructions.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed text-left mb-8">
                  Preview&nbsp;<Link to="/sample-reports" className="text-[#FF69B4] hover:underline cursor-pointer font-medium">Sample reports</Link>
                  {' '}for MegaMillions and PowerBall.
                </p>
                

              </div>
            </section>

            {/* Column 2 */}
            <section>
              <div className="bg-gray-50 rounded-xl p-8 h-full flex flex-col justify-center">
                <p className="text-gray-700 text-lg leading-relaxed mb-8 text-left">
                  Our on demand weekly research, analysis, and models for POWERBALL and MEGAMILLIONS is $79 for a&nbsp; license key thats good for 52 reports.
                </p>
                <div className="space-y-4">
                  <div id="paypal-container-8M77GKTPHMP4A"></div>
                  {paypalStatus === 'loading' && (
                    <div className="flex items-center justify-center py-4 text-gray-500 text-sm gap-2">
                      <svg className="animate-spin w-4 h-4 text-[#FF69B4]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                      </svg>
                      Loading payment button...
                    </div>
                  )}
                  {paypalStatus === 'error' && (
                    <div className="text-center py-4">
                      <p className="text-gray-500 text-sm mb-3">Payment button failed to load.</p>
                      <button
                        onClick={retryPaypal}
                        className="bg-[#FF69B4] text-white text-sm font-semibold px-5 py-2 rounded-lg hover:bg-[#FF1493] transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Column 3 */}
            <section>
              <div className="bg-gray-50 rounded-xl p-8 h-full">
                <p className="text-gray-700 text-lg leading-relaxed mb-4 text-left">
                  &gt;&gt;&nbsp;
                  <a
                    href="https://powerlottery-dashboard-2-246219247988.us-west1.run.app"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-[#FF69B4] hover:underline cursor-pointer font-medium"
                  >
                    Analytics and Reporting Engine
                  </a>
                  &nbsp;&lt;&lt;
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-6 text-left">
                  Need support? Complete the following form and we will contact you in 24 hours.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="support-form">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF69B4] text-sm"
                      required
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF69B4] text-sm"
                        required
                      />
                      <label className="flex items-center text-sm text-gray-600 whitespace-nowrap">
                        <input
                          type="checkbox"
                          name="isSubscriber"
                          checked={formData.isSubscriber}
                          onChange={handleInputChange}
                          className="mr-2 rounded focus:ring-[#FF69B4] cursor-pointer"
                        />
                        <div className="text-center leading-tight">
                          <div>Current</div>
                          <div>subscriber</div>
                        </div>
                      </label>
                    </div>
                    {emailError && (
                      <p className="text-red-500 text-sm mt-1">{emailError}</p>
                    )}
                  </div>
                  
                  <div>
                    <textarea
                      name="message"
                      placeholder="Your message..."
                      value={formData.message}
                      onChange={handleInputChange}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF69B4] text-sm h-32 resize-none"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      {formData.message.length}/500 characters
                    </p>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#FF69B4] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#FF1493] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                  
                  {submitStatus && (
                    <p className={`text-sm mt-2 ${submitStatus.includes('successfully') ? 'text-green-600' : 'text-red-500'}`}>
                      {submitStatus}
                    </p>
                  )}
                </form>
              </div>
            </section>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-black py-12 text-center border-t border-gray-700">
          <div className="max-w-4xl mx-auto px-6">
            <p className="text-white text-lg mb-4">
              The lottery is a game of chance. Winning is not guaranteed. Play responsibly.
            </p>
            <div className="flex justify-center items-center space-x-2 text-white">
              <span>©</span>
              <span className="font-medium">PowerLottery.co – All Rights Reserved.</span>
            </div>
            <div className="mt-4">
              <a 
                href="https://readdy.ai/?ref=logo" 
                className="text-gray-300 text-sm hover:text-white transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
